import { useEffect, useState } from 'react'
import { PERMISSIONS } from 'config/vars'
import {
  createReaderMutation,
  createTeacherMutation,
  deleteUserMutation,
  editUserMutation
} from 'api/mutations'
import {
  userClassifications,
  userButtonPlaceholders,
  userModalTitles,
  UserTypes,
  ModalTypes
} from '../constants'
import { validateEmail } from 'utils/validations'
import { post_event } from 'utils/analytics'
import { CLIENT_MANAGEMENT_USER_PAGE } from 'routes/paths'
import { useUserInfo } from 'utils/hooks/use-userInfo'

type SelectorItem = {
  id: string
  text: string
}

const useUserActionsModal = ({
  userType,
  userName,
  userId,
  userEmail,
  type,
  client,
  entityId,
  readerId,
  canManageUsers
}) => {
  const { user } = useUserInfo()
  const [modalTitle, setModalTitle] = useState<string>('')
  const [buttonLabel, setButtonLabel] = useState<string>('')
  const [selectorItems, setSelectorItems] = useState<SelectorItem[] | null>([])
  const [editedUserType, setEditedUserType] = useState<string>(userType || '')
  const [editedName, setEditedUserName] = useState<string>(userName || '')
  const [editedUserEmail, setEditedUserEmail] = useState<string>(
    userEmail || ''
  )
  const [editedEntityId, setEditedEntityId] = useState<string>(
    entityId?.toString() || ''
  )
  const [invalidEmail, setInvalidEmail] = useState<boolean>()
  const [canSubmitEntity, setCanSubmitEntity] = useState<boolean>(false)
  const [canCreateUsersAndAdmins, setCanCreateUsersAndAdmins] =
    useState<boolean>(false)
  useEffect(() => {
    setSelectorItems(canManageUsers ? userClassifications : null)
    setModalTitle(userModalTitles[type])
    setButtonLabel(userButtonPlaceholders[type])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const userEditedIsAdmin = editedUserType === UserTypes.admin
    const hasEntityId = Boolean(entityId || editedEntityId)

    const adminFormValidation =
      editedUserType && editedName && editedUserEmail && hasEntityId
    const editFormValidation = editedUserType && editedName
    const createFormValidation = editedUserType && editedName && hasEntityId

    const canSubmitForm = userEditedIsAdmin
      ? adminFormValidation
      : type === ModalTypes.edit
      ? editFormValidation
      : createFormValidation

    const canSubmit = canSubmitForm || type === ModalTypes.delete
    const emailIsValid = !invalidEmail

    setCanSubmitEntity(canSubmit && emailIsValid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, editedUserType, editedName, editedUserEmail, editedEntityId])

  const handleUserName = name => {
    setEditedUserName(name)
  }

  const handleUserType = userClassification => {
    setEditedUserType(userClassification)
  }

  const handleUserEmail = (email: string) => {
    setInvalidEmail(!validateEmail(email))
    setEditedUserEmail(email)
  }

  const handleAdminPermissions = (item, checked) => {
    if (item.id === 'canCreateUsersAndAdmins' && checked)
      setCanCreateUsersAndAdmins(true)
  }

  const handleEntityId = (value: string) => {
    setEditedEntityId(value)
  }

  const editUser = () => {
    const post_event_value = {
      user_id: userId,
      new_info: editedName,
      old_info: userName
    }
    client
      .mutate({
        mutation: editUserMutation,
        variables: {
          name: editedName,
          readerId: readerId
        }
      })

      .then(() => {
        post_event(
          `turmas-e-usuarios/edit_user_info`,
          JSON.stringify(post_event_value)
        )
        window.location.reload()
      })
      .catch(err => window.alert(`Oops, aconteceu um erro: ${err.message}`))
  }

  const createUser = () => {
    const variables = {
      entityId: entityId || parseInt(editedEntityId),
      name: editedName,
      email: editedUserEmail || null
    }

    if (editedUserType === UserTypes.admin) {
      variables['permissions'] = canCreateUsersAndAdmins
        ? [
            PERMISSIONS.CAN_CREATE_READER,
            PERMISSIONS.CAN_CREATE_ADMIN,
            PERMISSIONS.CAN_DELETE_READER,
            PERMISSIONS.CAN_DELETE_ADMIN
          ].join(' ')
        : ''
    }

    client
      .mutate({
        mutation:
          editedUserType === UserTypes.admin
            ? createTeacherMutation
            : createReaderMutation,
        variables
      })
      .then(response => {
        const userId =
          response?.data?.createTeacher?.id || response?.data?.createReader?.id

        if (userId && user.isAdmin) {
          window.location.href = CLIENT_MANAGEMENT_USER_PAGE(userId)
          return
        }

        window.location.reload()
      })
      .catch(err => window.alert(`Oops, aconteceu um erro: ${err.message}`))
  }

  const deleteUser = () => {
    client
      .mutate({
        mutation: deleteUserMutation,
        variables: { readerId }
      })
      .then(() => window.location.reload())
      .catch(err => window.alert(`Oops, aconteceu um erro: ${err.message}`))
  }

  const handleSubmit = () => {
    const submitFunction = {
      edit: editUser,
      create: createUser,
      delete: deleteUser
    }[type]
    submitFunction()
  }

  return {
    editedUserType,
    modalTitle,
    buttonLabel,
    selectorItems,
    canSubmitEntity,
    invalidEmail,
    handleSubmit,
    handleUserName,
    handleUserType,
    handleUserEmail,
    handleAdminPermissions,
    handleEntityId,
    editedEntityId
  }
}

export { useUserActionsModal }
